import { flow, makeObservable, observable } from "mobx";
import ApiBasedStore from "./ApiBasedStore";

export default class UserStore extends ApiBasedStore {
  userApi;
  users;
  link;

  constructor(rootStore, userApi) {
    super(rootStore);
    this.userApi = userApi;
    this.users = [];
    this.link = "";

    makeObservable(this, {
      users: observable,
      link: observable,
    });
  }

  find = flow(function* (email) {
    this.startRequest();

    try {
      const result = yield this.userApi.find(email);
      this.users = result;
    } catch (e) {
      this.onError(e);
    } finally {
      this.endRequest();
    }
  });

  update = flow(function* (uid, email) {
    this.startRequest();

    try {
      yield this.userApi.update(uid, email);
      const user = this.users.find((u) => u.uid === uid);
      user.email = email;
    } catch (e) {
      this.onError(e);
    } finally {
      this.endRequest();
    }
  });

  delete = flow(function* (uid) {
    this.startRequest();

    try {
      yield this.userApi.delete(uid);
      this.users = [];
    } catch (e) {
      this.onError(e);
    } finally {
      this.endRequest();
    }
  });

  clearOnboardingData = flow(function* (email) {
    this.startRequest();

    try {
      yield this.userApi.deleteOnboarding(email);
      this.users = [];
    } catch (e) {
      this.onError(e);
    } finally {
      this.endRequest();
    }
  });

  passwordReset = flow(function* (email) {
    this.startRequest();

    try {
      const result = yield this.userApi.generatePasswordResetLink(email);
      const originalLink = result?.password_reset_url || "";

      try {
        // Helper to extract params from a URL string
        const extract = (urlStr) => {
          const u = new URL(urlStr);
          return {
            mode: u.searchParams.get("mode"),
            oobCode: u.searchParams.get("oobCode"),
            apiKey: u.searchParams.get("apiKey"),
            lang: u.searchParams.get("lang"),
            nested: u.searchParams.get("link") || u.searchParams.get("ifl") || u.searchParams.get("afl"),
          };
        };

        let { mode, oobCode, apiKey, lang, nested } = extract(originalLink);

        // If params aren't on the top-level link (e.g., dynamic link), try nested
        if ((!oobCode || !mode || !apiKey) && nested) {
          try {
            const decoded = decodeURIComponent(nested);
            const nestedParams = extract(decoded);
            mode = mode || nestedParams.mode;
            oobCode = oobCode || nestedParams.oobCode;
            apiKey = apiKey || nestedParams.apiKey;
            lang = lang || nestedParams.lang;
          } catch (_) {}
        }

        // Require at least an oobCode to build the link; otherwise keep original
        if (!oobCode) {
          this.link = originalLink;
          return;
        }

        const isProd =
          process.env.REACT_APP_VERCEL_ENV === "production" || process.env.REACT_APP_APP_ENV === "production";
        const host = isProd ? "app.whering.co.uk" : "app-test.whering.co.uk";
        const out = new URL(`https://${host}/auth-action`);
        out.searchParams.set("mode", mode || "resetPassword");
        out.searchParams.set("oobCode", oobCode);
        if (apiKey) out.searchParams.set("apiKey", apiKey);
        out.searchParams.set("lang", lang || "en");
        this.link = out.toString();
      } catch (_) {
        this.link = originalLink;
      }
    } catch (e) {
      this.onError(e);
    } finally {
      this.endRequest();
    }
  });
}
